import React, { useMemo, useState, useEffect } from "react";
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Table,
  Modal,
  Collapse,
} from "react-bootstrap";
import Layout from "../../layout/Layout";
import "../../styles/StandardDocument.css";
import { useAuth } from "../../context/AuthContext";
import { TrainingEnum } from "../../common/trainingTypes";
import { createDocumentHandleChange } from "../../helper/createDocumentHandleChange";

import {
  uploadIsgPersonel,
  getPersonelIsg,
  updateIsg,
} from "../../../api/DocumentApi/isgApi";
import { eksikAlanlar, yuklemeHataMesaji } from "../../../utils/yuklemeHata";

function IsgPersonelCertifica() {
  // ------------------- STATE & AUTH -------------------
  const { user } = useAuth();

  const [filters, setFilters] = useState({
    search: "",
    cinsiyet: "",
    calismaDurumu: "",
    gorev: "",
    egitimDurumu: "",
    riskSinifi: "",
    onlyReminder: false,
    trainingCategory: "",
  });

  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [openRow, setOpenRow] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);

  const [feedback, setFeedback] = useState({
    show: false,
    type: "success",
    message: "",
  });

  const [newDoc, setNewDoc] = useState({
    calismaDurumu: "",
    ad: "",
    soyad: "",
    tcNo: "",
    cinsiyet: "",
    dogumTarihi: "",
    iseGirisTarihi: "",
    yas: "",
    file: null,
    yasAraligi: "",
    firmaTehlikeSinifi: "",
    egitimDurumu: "",
    gorev: "",
    isGuvenligiEgitim: "",
    kkdZimmet: "",
    reminderActive: false,
    startDate: "",
    endDate: "",
    note: "",
    notifyBefore: "",
    trainings: [],
  });

  const [docs, setDocs] = useState([]);

  // ------------------- DEFAULT TRAININGS -------------------
  const defaultTrainings = useMemo(
    () =>
      Object.keys(TrainingEnum).map((key) => ({
        trainingName: key,
      })),
    []
  );

  // ------------------- FETCH DATA -------------------
  useEffect(() => {
    const fetchDocs = async () => {
      try {
        const res = await getPersonelIsg();
        if (res.data.statusCode === 200) {
          setDocs(res.data.data);
        }
      } catch (err) {
        console.error("Belgeler çekilemedi:", err);
      }
    };
    fetchDocs();
  }, []);

  // ------------------- FILTERING -------------------
  const filteredDocs = docs.filter((d) => {
    const searchMatch = `${d.firstName} ${d.lastName}`
      .toLowerCase()
      .includes(filters.search.toLowerCase());

    const genderMatch = !filters.cinsiyet
      ? true
      : d.gender?.toUpperCase() === filters.cinsiyet;

    const durumMatch = !filters.calismaDurumu
      ? true
      : (d.employmentStatus || "").toUpperCase() === filters.calismaDurumu;

    const gorevMatch = !filters.gorev ? true : d.position === filters.gorev;

    const egitimMatch = !filters.egitimDurumu
      ? true
      : d.educationLevel === filters.egitimDurumu;

    const trainingMatch = !filters.trainingCategory
      ? true
      : d.trainings?.some((t) => t.trainingName === filters.trainingCategory);

    const reminderMatch = !filters.onlyReminder
      ? true
      : d.trainings?.some((t) => {
          const expiry = t.certificateExpiryDate
            ? new Date(t.certificateExpiryDate)
            : null;
          if (!expiry) return false;
          const daysLeft = Math.ceil(
            (expiry - new Date()) / (1000 * 60 * 60 * 24)
          );
          return daysLeft <= 30 && daysLeft >= 0;
        });

    return (
      searchMatch &&
      genderMatch &&
      durumMatch &&
      gorevMatch &&
      egitimMatch &&
      trainingMatch &&
      reminderMatch
    );
  });

  // ------------------- SORTING -------------------
  const sortedDocs = [...filteredDocs].sort((a, b) => {
    const aHasRequired = a.trainings?.some((t) => t.required);
    const bHasRequired = b.trainings?.some((t) => t.required);

    if (aHasRequired && !bHasRequired) return -1;
    if (!aHasRequired && bHasRequired) return 1;

    const getNextExpiry = (doc) => {
      const validDates = doc.trainings
        ?.map((t) => new Date(t.certificateExpiryDate))
        .filter((d) => !isNaN(d));
      return validDates?.length
        ? Math.min(...validDates.map((d) => d.getTime()))
        : Infinity;
    };

    return getNextExpiry(a) - getNextExpiry(b);
  });

  // ------------------- MODAL CONTROL -------------------
  const handleShow = () => setShowModal(true);

  const handleClose = () => {
    setShowModal(false);
    setNewDoc({
      calismaDurumu: "",
      ad: "",
      soyad: "",
      tcNo: "",
      cinsiyet: "",
      dogumTarihi: "",
      iseGirisTarihi: "",
      firmaTehlikeSinifi: "",
      egitimDurumu: "",
      gorev: "",
      trainings: [],
    });
  };

  //  Düzenleme Modalini Aç
  const handleShowEdit = (doc) => {
    setEditingDoc(doc);
    setNewDoc({
      ...doc,
      trainings:
        doc.trainings && doc.trainings.length > 0
          ? doc.trainings
          : defaultTrainings,
    });
    setShowEditModal(true);
  };

  const handleCloseEdit = () => {
    setShowEditModal(false);
    setEditingDoc(null);
    setNewDoc({
      calismaDurumu: "",
      ad: "",
      soyad: "",
      tcNo: "",
      cinsiyet: "",
      dogumTarihi: "",
      iseGirisTarihi: "",
      firmaTehlikeSinifi: "",
      egitimDurumu: "",
      gorev: "",
      trainings: [],
    });
  };

  // ------------------- INPUT CHANGE -------------------
const handleChange = createDocumentHandleChange({
  setNewDoc,
  getIsEditMode: () => Boolean(editingDoc?._id),
});

  const validateDoc = (doc) => {
    if (!doc.title?.trim()) return "Başlık boş olamaz!";
    if (!doc.description?.trim()) return "Açıklama boş olamaz!";
    if (doc.reminderActive) {
      if (!doc.startDate || !doc.endDate) {
        return "Başlangıç ve bitiş tarihleri seçilmeli!";
      }
      if (!doc.notifyBefore || Number(doc.notifyBefore) <= 0) {
        return "Bildirim günü 0'dan büyük olmalı!";
      }
    }

    return null; //  her şey yolunda
  };

  //  Eğitim Alanı Güncelleme
  const handleTrainingChange = (e, index) => {
    const { name, value } = e.target;
    setNewDoc((prev) => {
      const updatedTrainings = [...(prev.trainings || [])];
      updatedTrainings[index] = { ...updatedTrainings[index], [name]: value };
      return { ...prev, trainings: updatedTrainings };
    });
  };
  // Yeni eğitim ekleme
  const handleAddTraining = () => {
    setNewDoc((prev) => ({
      ...prev,
      trainings: [
        ...(prev.trainings || []),
        {
          trainingName: "",
          required: false,
          examStatus: "None",
          topics: "",
          certificateStartDate: "",
          certificateExpiryDate: "",
          certificateReceived: false,
          certificateNotReceived: false,
        },
      ],
    }));
  };

  // Eğitim silme
  const handleRemoveTraining = (index) => {
    setNewDoc((prev) => ({
      ...prev,
      trainings: prev.trainings.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    if (!user) {
      alert("Önce giriş yapmalısınız!");
      return;
    }
    const eksik = eksikAlanlar(newDoc);
    if (eksik) {
      setFeedback({ show: true, type: "error", message: eksik });
      return;
    }

    //  Validation check
    const error = validateDoc(newDoc);
    if (error) {
      setFeedback({ show: true, type: "danger", message: error });
      return;
    }

    try {
      const reminderData = newDoc.reminderActive
        ? {
            reminderActive: true,
            startDate: newDoc.startDate,
            endDate: newDoc.endDate,
            note: newDoc.note,
            notifyBefore: newDoc.notifyBefore || 30,
          }
        : null;

      const result = await uploadIsgPersonel(
        user._id || user.id,
        newDoc.file,
        newDoc.title,
        newDoc.description,
        reminderData
      );

      if (result.data.statusCode === 200) {
        const res = await getPersonelIsg();
        if (res.data.statusCode === 200) {
          setDocs(res.data.data);
        }

        handleClose();
        setNewDoc({
          title: "",
          description: "",
          file: null,
          reminderActive: false,
          startDate: "",
          endDate: "",
          note: "",
          notifyBefore: "",
        });

        setFeedback({
          show: true,
          type: "success",
          message: "Dosya başarıyla yüklendi ",
        });
      } else {
        setFeedback({
          show: true,
          type: "danger",
          message: "Yükleme başarısız oldu!",
        });
      }
    } catch (err) {
      console.error(err);
      setFeedback({ show: true, type: "danger", message: yuklemeHataMesaji(err) });
    }
  };

  const handleEditSave = async () => {
    if (!user || !editingDoc) {
      alert("Önce giriş yapmalısınız!");
      return;
    }
    const eksik = eksikAlanlar(newDoc, { dosyaZorunlu: false });
    if (eksik) {
      setFeedback({ show: true, type: "error", message: eksik });
      return;
    }
    try {
      const reminderData = newDoc.reminderActive
        ? {
            reminderActive: true,
            startDate: newDoc.startDate,
            endDate: newDoc.endDate,
            note: newDoc.note,
            notifyBefore: newDoc.notifyBefore || 30,
          }
        : null;

      const result = await updateIsg(
        editingDoc._id,
        user.id || user._id,
        newDoc.file,
        newDoc.title,
        newDoc.description,
        reminderData
      );

      if (result.data.statusCode === 200) {
        const res = await getPersonelIsg();
        if (res.data.statusCode === 200) {
          setDocs(res.data.data);
        }
        handleCloseEdit();
        setFeedback({
          show: true,
          type: "success",
          message: "Dosya başarıyla güncellendi ",
        });
      } else {
        setFeedback({
          show: true,
          type: "error",
          message: "Güncelleme başarısız oldu!",
        });
      }
    } catch (err) {
      console.error(err);
      setFeedback({ show: true, type: "error", message: yuklemeHataMesaji(err) });
    }
  };

  // ------------------- DEFAULT TRAINING FILLER -------------------
  useEffect(() => {
    if (showEditModal && editingDoc && !editingDoc.trainings?.length) {
      setNewDoc((prev) => ({
        ...prev,
        trainings: defaultTrainings,
      }));
    }
  }, [showEditModal, editingDoc, defaultTrainings]);

  return (
    <Layout>
      <h2 className="fw-bold mb-4">Personel Eğitim Takip</h2>
      <Card className="border-0 bg-transparent mb-3">
        <div
          className="d-flex justify-content-end align-items-center"
          style={{ minHeight: "40px" }}
        >
          <Button variant="primary" size="sm" onClick={handleShow}>
            + Yeni Ekle
          </Button>
        </div>
      </Card>

      <Card className="p-3 mb-4 shadow-sm border-0">
        <Row className="align-items-center g-3">
          {/* 🔍 Arama */}
          <Col md={3}>
            <Form.Control
              type="text"
              placeholder="Ad veya Soyad ile ara..."
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
            />
          </Col>

          {/* 👤 Cinsiyet */}
          <Col md={2}>
            <Form.Select
              value={filters.cinsiyet}
              onChange={(e) =>
                setFilters({ ...filters, cinsiyet: e.target.value })
              }
            >
              <option value="">Cinsiyet (Hepsi)</option>
              <option value="ERKEK">Erkek</option>
              <option value="KADIN">Kadın</option>
            </Form.Select>
          </Col>

          {/* 🏢 Çalışma Durumu */}
          <Col md={2}>
            <Form.Select
              value={filters.calismaDurumu}
              onChange={(e) =>
                setFilters({ ...filters, calismaDurumu: e.target.value })
              }
            >
              <option value="">Durum (Hepsi)</option>
              <option value="AKTİF">Aktif</option>
              <option value="PASİF">Pasif</option>
            </Form.Select>
          </Col>

          {/* 🧰 Görev */}
          <Col md={2}>
            <Form.Select
              value={filters.gorev}
              onChange={(e) =>
                setFilters({ ...filters, gorev: e.target.value })
              }
            >
              <option value="">Görev (Hepsi)</option>
              {[...new Set(docs.map((d) => d.position).filter(Boolean))].map(
                (pos, i) => (
                  <option key={i} value={pos}>
                    {pos}
                  </option>
                )
              )}
            </Form.Select>
          </Col>

          {/* 🎓 Eğitim Durumu */}
          <Col md={2}>
            <Form.Select
              value={filters.egitimDurumu}
              onChange={(e) =>
                setFilters({ ...filters, egitimDurumu: e.target.value })
              }
            >
              <option value="">Eğitim Durumu (Hepsi)</option>
              {[
                ...new Set(docs.map((d) => d.educationLevel).filter(Boolean)),
              ].map((edu, i) => (
                <option key={i} value={edu}>
                  {edu}
                </option>
              ))}
            </Form.Select>
          </Col>

          {/* ⚠️ Hatırlatma (öncelikli kayıtlar) */}
          <Col md="auto">
            <Form.Check
              inline
              type="switch"
              id="reminderSwitch"
              label="Hatırlatma Öncelikli"
              checked={filters.onlyReminder}
              onChange={(e) =>
                setFilters({ ...filters, onlyReminder: e.target.checked })
              }
            />
          </Col>

          {/* 📚 Eğitim Kategorisi */}
          <Col md={2}>
            <Form.Select
              value={filters.trainingCategory}
              onChange={(e) =>
                setFilters({ ...filters, trainingCategory: e.target.value })
              }
            >
              <option value="">Eğitim Kategorisi (Hepsi)</option>
              {[
                ...new Set(
                  docs.flatMap((d) =>
                    (d.trainings || []).map((t) => t.trainingName)
                  )
                ),
              ]
                .filter(Boolean)
                .map((cat, i) => (
                  <option key={i} value={cat}>
                    {cat}
                  </option>
                ))}
            </Form.Select>
          </Col>

          {/* 🧹 Temizle */}
          <Col md="auto">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() =>
                setFilters({
                  search: "",
                  cinsiyet: "",
                  calismaDurumu: "",
                  gorev: "",
                  egitimDurumu: "",
                  riskSinifi: "",
                  onlyReminder: false,
                  trainingCategory: "",
                })
              }
            >
              Temizle
            </Button>
          </Col>
        </Row>
      </Card>

      <Card className="shadow-sm border-0">
        <Table hover responsive className="mb-0 align-middle">
          <thead className="table-light">
            <tr>
              <th></th>
              <th>Ad Soyad</th>
              <th>TC No</th>
              <th>Cinsiyet</th>
              <th>Çalışma Durumu</th>
              <th>Görev</th>
              <th>Eğitim Durumu</th>
              <th>Firma Tehlike Sınıfı</th>
              <th>Hatırlama</th>
              <th>Yüklenme Tarihi</th>
              {user?.permissions.includes("isg_update") && <th>Düzenle</th>}
            </tr>
          </thead>

          <tbody>
            {sortedDocs.length > 0 ? (
              sortedDocs.map((d) => (
                <React.Fragment key={d._id}>
                  <tr>
                    <td>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() =>
                          setOpenRow(openRow === d._id ? null : d._id)
                        }
                      >
                        {openRow === d._id ? "▲" : "▼"}
                      </Button>
                    </td>

                    <td>{`${d.firstName || ""} ${d.lastName || ""}`}</td>
                    <td>{d.nationalId || "-"}</td>
                    <td>{d.gender || "-"}</td>
                    <td>
                      {d.isActive ? (
                        <span className="badge bg-success">Aktif</span>
                      ) : (
                        <span className="badge bg-secondary">Pasif</span>
                      )}
                    </td>
                    <td>{d.position || "-"}</td>
                    <td>{d.educationLevel || "-"}</td>
                    <td>{d.companyRiskClass || "-"}</td>
                    <td>{d.safetyTrainingDate || "-"}</td>
                    <td>{d.hireDate || "-"}</td>

                    {user?.permissions.includes("isg_update") && (
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleShowEdit(d)}
                        >
                          Düzenle
                        </Button>
                      </td>
                    )}
                  </tr>

                  {/* ↓↓↓ Bu Collapse kısmında d.trainings.map olur ↓↓↓ */}
                  <tr>
                    <td colSpan="12" className="p-0">
                      <Collapse in={openRow === d._id}>
                        <div className="bg-light p-3">
                          <h6 className="fw-bold mb-2">
                            Eğitim ve Sertifika Bilgileri
                          </h6>

                          {d.trainings && d.trainings.length > 0 ? (
                            <Table size="sm" bordered responsive>
                              <thead>
                                <tr>
                                  <th>Eğitim Adı</th>
                                  <th>Alması Gerek</th>
                                  <th>Sınav Durumu</th>
                                  <th>Belge Başlangıç</th>
                                  <th>Belge Geçerlilik</th>
                                  <th>Sertifika Durumu</th>
                                </tr>
                              </thead>
                              <tbody>
                                {d.trainings.map((t, idx) => {
                                  const today = new Date();
                                  const expiry = t.certificateExpiryDate
                                    ? new Date(t.certificateExpiryDate)
                                    : null;
                                  const daysLeft = expiry
                                    ? Math.ceil(
                                        (expiry - today) / (1000 * 60 * 60 * 24)
                                      )
                                    : null;

                                  let rowClass = "";
                                  if (t.required) rowClass = "table-warning";
                                  if (daysLeft !== null && daysLeft <= 30)
                                    rowClass = "table-danger";

                                  return (
                                    <tr key={idx} className={rowClass}>
                                      <td>
                                        {t.required && (
                                          <span
                                            title="Alması Gerek"
                                            className="me-1"
                                          >
                                            ⚠️
                                          </span>
                                        )}
                                        {t.trainingName || "-"}
                                      </td>
                                      <td>{t.required ? "Evet" : "Hayır"}</td>
                                      <td>{t.examStatus || "-"}</td>
                                      <td>{t.certificateStartDate || "-"}</td>
                                      <td>{t.certificateExpiryDate || "-"}</td>
                                      <td>
                                        {t.certificateReceived ? (
                                          <span className="badge bg-success">
                                            Geldi
                                          </span>
                                        ) : t.certificateNotReceived ? (
                                          <span className="badge bg-danger">
                                            Gelmedi
                                          </span>
                                        ) : (
                                          <span className="badge bg-secondary">
                                            -
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          ) : (
                            <p className="text-muted">
                              Bu personele ait eğitim kaydı bulunamadı.
                            </p>
                          )}
                        </div>
                      </Collapse>
                    </td>
                  </tr>
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan="12" className="text-center py-4 text-muted">
                  Kayıt bulunamadı
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Card>

      <Modal
        show={showModal}
        onHide={handleClose}
        centered
        size="xl"
        fullscreen="md-down"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Yeni İSG Personeli Ekle</Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: "2rem" }}>
          <Form>
            <Row className="gy-4 gx-5">
              {/* SOL TARAF - PERSONEL BİLGİLERİ */}
              <Col md={4} className="border-end pe-4">
                <h5 className="mb-3">Personel Bilgileri</h5>
                <Row className="g-3">
                  {[
                    ["Ad", "ad"],
                    ["Soyad", "soyad"],
                    ["TC No", "tcNo"],
                    ["Cinsiyet", "cinsiyet"],
                    ["Çalışma Durumu", "calismaDurumu"],
                    ["Firma Tehlike Sınıfı", "firmaTehlikeSinifi"],
                    ["Eğitim Durumu", "egitimDurumu"],
                    ["Görev", "gorev"],
                    ["İşe Giriş Tarihi", "iseGirisTarihi", "date"],
                    ["KKD Zimmet", "kkdZimmet", "date"],
                  ].map(([label, name, type = "text"], i) => (
                    <Col xs={12} key={i}>
                      <Form.Group>
                        <Form.Label>{label}</Form.Label>
                        {name === "cinsiyet" ? (
                          <Form.Select
                            name={name}
                            value={newDoc[name] || ""}
                            onChange={handleChange}
                          >
                            <option value="">Seçiniz</option>
                            <option value="ERKEK">Erkek</option>
                            <option value="KADIN">Kadın</option>
                          </Form.Select>
                        ) : name === "calismaDurumu" ? (
                          <Form.Select
                            name={name}
                            value={newDoc[name] || ""}
                            onChange={handleChange}
                          >
                            <option value="AKTİF">AKTİF</option>
                            <option value="PASİF">PASİF</option>
                          </Form.Select>
                        ) : name === "firmaTehlikeSinifi" ? (
                          <Form.Select
                            name={name}
                            value={newDoc[name] || ""}
                            onChange={handleChange}
                          >
                            <option value="">Seçiniz</option>
                            <option value="AZ TEHLİKELİ">AZ TEHLİKELİ</option>
                            <option value="ÇOK TEHLİKELİ">ÇOK TEHLİKELİ</option>
                          </Form.Select>
                        ) : name === "egitimDurumu" ? (
                          <Form.Select
                            name={name}
                            value={newDoc[name] || ""}
                            onChange={handleChange}
                          >
                            <option value="">Seçiniz</option>
                            <option value="LİSANS">LİSANS</option>
                            <option value="ÖNLİSANS">ÖNLİSANS</option>
                            <option value="LİSE">LİSE</option>
                            <option value="ORTAOKUL">ORTAOKUL</option>
                            <option value="İLKOKUL">İLKOKUL</option>
                          </Form.Select>
                        ) : (
                          <Form.Control
                            type={type}
                            name={name}
                            value={newDoc[name] || ""}
                            onChange={handleChange}
                          />
                        )}
                      </Form.Group>
                    </Col>
                  ))}
                </Row>
              </Col>

              {/* SAĞ TARAF - EĞİTİM BİLGİLERİ */}
              <Col md={8} className="ps-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="mb-0">Eğitim Bilgileri</h5>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleAddTraining}
                  >
                    + Yeni Eğitim
                  </Button>
                </div>

                <div className="d-flex flex-column gap-3">
                  {(newDoc.trainings || []).map((training, index) => (
                    <div
                      key={index}
                      className="border rounded-3 px-3 py-3 bg-light-subtle d-flex flex-wrap align-items-end gap-3"
                    >
                      {/* 🎓 Eğitim */}
                      <div
                        className="d-flex flex-column"
                        style={{ width: "220px" }}
                      >
                        <Form.Label className="text-muted mb-1 small">
                          Eğitim
                        </Form.Label>
                        <Form.Select
                          name="trainingName"
                          value={training.trainingName || ""}
                          onChange={(e) => handleTrainingChange(e, index)}
                          className="form-select-sm"
                        >
                          <option value="">Eğitim Seçin...</option>
                          {Object.entries(TrainingEnum).map(([key, label]) => (
                            <option key={key} value={key}>
                              {label}
                            </option>
                          ))}
                        </Form.Select>
                      </div>

                      {/* 📋 Alması Gerek */}
                      <div
                        className="d-flex flex-column"
                        style={{ width: "100px" }}
                      >
                        <Form.Label className="text-muted mb-1 small">
                          Gerekli
                        </Form.Label>
                        <Form.Check
                          type="checkbox"
                          checked={training.required || false}
                          onChange={(e) =>
                            handleTrainingChange(
                              {
                                target: {
                                  name: "required",
                                  value: e.target.checked,
                                },
                              },
                              index
                            )
                          }
                        />
                      </div>

                      {/* 📅 Başlangıç */}
                      <div
                        className="d-flex flex-column"
                        style={{ width: "150px" }}
                      >
                        <Form.Label className="text-muted mb-1 small">
                          Başlangıç
                        </Form.Label>
                        <Form.Control
                          type="date"
                          name="certificateStartDate"
                          value={training.certificateStartDate || ""}
                          onChange={(e) => handleTrainingChange(e, index)}
                          className="form-control-sm"
                        />
                      </div>

                      {/* 📅 Bitiş */}
                      <div
                        className="d-flex flex-column"
                        style={{ width: "150px" }}
                      >
                        <Form.Label className="text-muted mb-1 small">
                          Bitiş
                        </Form.Label>
                        <Form.Control
                          type="date"
                          name="certificateExpiryDate"
                          value={training.certificateExpiryDate || ""}
                          onChange={(e) => handleTrainingChange(e, index)}
                          className="form-control-sm"
                        />
                      </div>

                      {/* 🧾 Sınav Durumu */}
                      <div
                        className="d-flex flex-column"
                        style={{ width: "130px" }}
                      >
                        <Form.Label className="text-muted mb-1 small">
                          Sınav
                        </Form.Label>
                        <Form.Select
                          name="examStatus"
                          value={training.examStatus || "None"}
                          onChange={(e) => handleTrainingChange(e, index)}
                          className="form-select-sm"
                        >
                          <option value="None">Yok</option>
                          <option value="Pending">Beklemede</option>
                          <option value="Passed">Geçti</option>
                          <option value="Failed">Kaldı</option>
                        </Form.Select>
                      </div>

                      {/* 🎖️ Sertifika */}
                      <div
                        className="d-flex flex-column"
                        style={{ width: "110px" }}
                      >
                        <Form.Label className="text-muted mb-1 small">
                          Sertifika
                        </Form.Label>
                        <Form.Check
                          type="checkbox"
                          checked={training.certificateReceived || false}
                          onChange={(e) =>
                            handleTrainingChange(
                              {
                                target: {
                                  name: "certificateReceived",
                                  value: e.target.checked,
                                },
                              },
                              index
                            )
                          }
                        />
                      </div>

                      {/* ❌ Sil */}
                      <div className="d-flex flex-column align-items-center ms-auto">
                        <Form.Label className="text-muted mb-1 small">
                          İşlem
                        </Form.Label>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRemoveTraining(index)}
                        >
                          ×
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            İptal
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Kaydet
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Düzenleme Modalı */}
      <Modal
        show={showEditModal}
        onHide={handleCloseEdit}
        centered
        size="xl"
        fullscreen="md-down"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Personel Bilgilerini Düzenle</Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: "2rem" }}>
          <Form>
            <Row className="gy-4 gx-5">
              {/* SOL TARAF */}
              <Col md={4} className="border-end pe-4">
                <h5 className="mb-3">Personel Bilgileri</h5>
                <Row className="g-3">
                  {[
                    ["Ad", "firstName"],
                    ["Soyad", "lastName"],
                    ["TC No", "nationalId"],
                    ["Cinsiyet", "gender"],
                    ["Çalışma Durumu", "employmentStatus"],
                    ["Firma Tehlike Sınıfı", "companyRiskClass"],
                    ["Eğitim Durumu", "educationLevel"],
                    ["Görev", "position"],
                    ["İşe Giriş Tarihi", "hireDate", "date"],
                    ["KKD Zimmet", "ppeAssignmentDate", "date"],
                  ].map(([label, name, type = "text"], i) => (
                    <Col xs={12} key={i}>
                      <Form.Group>
                        <Form.Label>{label}</Form.Label>
                        {name === "gender" ? (
                          <Form.Select
                            name={name}
                            value={newDoc[name] || ""}
                            onChange={handleChange}
                          >
                            <option value="">Seçiniz</option>
                            <option value="ERKEK">Erkek</option>
                            <option value="KADIN">Kadın</option>
                          </Form.Select>
                        ) : name === "employmentStatus" ? (
                          <Form.Select
                            name={name}
                            value={newDoc[name] || ""}
                            onChange={handleChange}
                          >
                            <option value="AKTİF">AKTİF</option>
                            <option value="PASİF">PASİF</option>
                          </Form.Select>
                        ) : (
                          <Form.Control
                            type={type}
                            name={name}
                            value={newDoc[name] || ""}
                            onChange={handleChange}
                          />
                        )}
                      </Form.Group>
                    </Col>
                  ))}
                </Row>
              </Col>

              {/* SAĞ TARAF */}
              <Col md={8} className="ps-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="mb-0">Eğitim Bilgileri</h5>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleAddTraining}
                  >
                    + Yeni Eğitim
                  </Button>
                </div>

                <div className="d-flex flex-column gap-4">
                  {(newDoc.trainings || []).map((training, index) => (
                    <div
                      key={index}
                      className="border rounded-4 p-4 shadow-sm bg-light d-flex flex-wrap align-items-center justify-content-between gap-3"
                    >
                      {/* Eğitim Adı */}
                      <div className="flex-grow-1">
                        <strong className="d-block mb-2">
                          {TrainingEnum[training.trainingName] ||
                            training.trainingName}
                        </strong>
                        <div className="d-flex flex-wrap gap-3 align-items-center">
                          <Form.Check
                            type="checkbox"
                            label="Alması Gerek"
                            checked={training.required || false}
                            onChange={(e) =>
                              handleTrainingChange(
                                {
                                  target: {
                                    name: "required",
                                    value: e.target.checked,
                                  },
                                },
                                index
                              )
                            }
                          />

                          <Form.Control
                            type="date"
                            name="certificateStartDate"
                            value={training.certificateStartDate || ""}
                            onChange={(e) => handleTrainingChange(e, index)}
                            style={{ width: "170px" }}
                          />

                          <Form.Control
                            type="date"
                            name="certificateExpiryDate"
                            value={training.certificateExpiryDate || ""}
                            onChange={(e) => handleTrainingChange(e, index)}
                            style={{ width: "170px" }}
                          />

                          <Form.Select
                            name="examStatus"
                            value={training.examStatus || "None"}
                            onChange={(e) => handleTrainingChange(e, index)}
                            style={{ width: "150px" }}
                          >
                            <option value="None">Yok</option>
                            <option value="Pending">Beklemede</option>
                            <option value="Passed">Geçti</option>
                            <option value="Failed">Kaldı</option>
                          </Form.Select>

                          <Form.Check
                            type="checkbox"
                            label="Sertifika"
                            checked={training.certificateReceived || false}
                            onChange={(e) =>
                              handleTrainingChange(
                                {
                                  target: {
                                    name: "certificateReceived",
                                    value: e.target.checked,
                                  },
                                },
                                index
                              )
                            }
                          />
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveTraining(index)}
                          >
                            × Kaldır
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseEdit}>
            İptal
          </Button>
          <Button variant="primary" onClick={handleEditSave}>
            Güncelle
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Feedback Modal */}
      <Modal
        show={feedback.show}
        onHide={() => setFeedback({ ...feedback, show: false })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {feedback.type === "success" ? "Başarılı" : "Hata"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{feedback.message}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setFeedback({ ...feedback, show: false })}
          >
            Kapat
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
}

export default IsgPersonelCertifica;
